import * as Dialog from '@radix-ui/react-dialog';
import { Check, MapPin, X } from 'lucide-react';
import { useState } from 'react';
import { useZones } from '@/hooks/useStorefront';
import { cn, formatPrice } from '@/lib/utils';
import { useZoneStore } from '@/stores/zone.store';
import { LoadingState } from '@/components/ui/states';

interface ZonePickerProps {
  currentZoneName: string;
  currentZoneSlug: string;
  triggerClassName?: string;
  label?: string;
}

/**
 * Bölge seçimi TÜM SİTE için geçerlidir: burada yapılan değişiklik ana
 * sayfadaki "bugün teslim" göstergelerini de tazeler (query anahtarları
 * bölgeyi içerir).
 *
 * Mobilde alttan açılan sheet, masaüstünde ortalanmış diyalog.
 */
export function ZonePicker({
  currentZoneName,
  currentZoneSlug,
  triggerClassName,
  label = 'Bölgeyi değiştir',
}: ZonePickerProps) {
  const [open, setOpen] = useState(false);
  const { data: zones, isPending, isError } = useZones();
  const setZone = useZoneStore((state) => state.setZone);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        className={cn(
          'inline-flex items-center gap-1.5 text-sm text-leaf-600',
          'transition-colors duration-fast hover:text-leaf-800',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf-700',
          triggerClassName,
        )}
      >
        <MapPin className="size-3.5" aria-hidden />
        {label}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in" />

        <Dialog.Content
          className={cn(
            'fixed z-50 bg-surface shadow-lift',
            // Mobil: alttan sheet · Desktop: ortalanmış
            'inset-x-0 bottom-0 rounded-t-lg pb-[calc(env(safe-area-inset-bottom,0px)+1rem)]',
            'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md',
            'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:pb-0',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom sm:data-[state=open]:zoom-in-95',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <Dialog.Title className="font-serif text-lg text-ink">
                Nereye gönderiyorsunuz?
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-ink-muted">
                Teslimat süresi ve ücreti bölgeye göre değişir.
              </Dialog.Description>
            </div>

            <Dialog.Close
              aria-label="Kapat"
              className="-mr-1 flex size-9 shrink-0 items-center justify-center rounded-sm text-ink-muted hover:bg-surface-sunken hover:text-ink"
            >
              <X className="size-[18px]" aria-hidden />
            </Dialog.Close>
          </div>

          <div className="max-h-[60svh] overflow-y-auto p-2">
            {isPending ? (
              <LoadingState label="Bölgeler yükleniyor" />
            ) : isError || !zones?.length ? (
              <p className="px-3 py-6 text-center text-sm text-ink-muted">
                Bölge listesi şu anda alınamadı.
              </p>
            ) : (
              <ul className="space-y-1">
                {zones.map((zone) => {
                  const selected = zone.slug === currentZoneSlug;

                  return (
                    <li key={zone.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setZone(zone.slug);
                          setOpen(false);
                        }}
                        aria-current={selected ? 'true' : undefined}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-sm px-3 py-3 text-left',
                          'transition-colors duration-fast ease-flow',
                          selected
                            ? 'bg-leaf-100 text-leaf-900'
                            : 'hover:bg-surface-sunken',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-base text-ink">{zone.name}</span>
                          <span className="mt-0.5 block text-xs text-ink-muted">
                            Teslimat {formatPrice(zone.fee)}
                            {zone.freeThreshold
                              ? ` · ${formatPrice(zone.freeThreshold)} üzeri ücretsiz`
                              : ''}
                            {zone.sameDayAvailable
                              ? ` · Aynı gün ${zone.sameDayCutoff}’a kadar`
                              : ' · Aynı gün teslimat yok'}
                          </span>
                        </span>

                        {selected ? (
                          <Check className="size-4 shrink-0 text-leaf-700" aria-hidden />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <p className="border-t border-line px-5 py-3 text-xs text-ink-subtle">
            Şu anki bölge: {currentZoneName}
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
